<template>
    <ion-row v-if="!route.params.listId">
        <ion-col :class="cssClass">
            <span class="fw-b">{{ $t('label.list') }}</span>
            <select-simple
                v-model="timerSettings.listId"
                class="mt-1 grey4-grey4-background inherit-background purple-purple-color max-width chevron-select max-width-custom"
                :placeholder="$t('target.list')"
                :options="listStore.lists.data"
                valueKey="id"
                textKey="name"
            />
        </ion-col>
    </ion-row>

    <ion-row class="width-custom">
        <ion-col :class="cssClass">
            <span class="fw-b">{{ $t('label.name') }}</span>
            <input-simple
                class="mt-1 unset-max-width"
                input-class="grey4-grey4-background"
                v-model="timerSettings.name"
            />
        </ion-col>
    </ion-row>

    <ion-row class="pt-1 width-custom">
        <ion-col :class="cssClass">
            <span class="fw-b">{{ $t('messages.list.repeatEach1') }}</span>
            <select-simple
                class="mt-1 grey4-grey4-background chevron-select unset-max-width"
                v-model="timerSettings.type"
                :options="repeatOptions"
            />
        </ion-col>
    </ion-row>

    <ion-row class="purple-purple-color mt-1" v-if="timerSettings.type === REPEAT_CYCLE.ONCE">
        <ion-col class="center py-2">
            <ion-datetime
                class="grey4-grey4-background custom-datetime card-border order-filter-month"
                :min="today ?? ''"
                :max="(new Date().getFullYear() + 1).toString()"
                v-model="timerSettings.date"
                :locale="$i18n.locale"
                :first-day-of-week="1"
            >
                <span slot="time-label">{{ $t('label.time') }}</span>
            </ion-datetime>
        </ion-col>
    </ion-row>

    <ion-row class="d-flex purple-light-color mt-1" v-if="timerSettings.type === REPEAT_CYCLE.WEEK">
        <ion-col class="center-y" :class="cssClass"
                 style="gap: .75rem;">
            <span>{{ $t('messages.list.repeatEach2') }}</span>
            <input-simple
                :type="INPUT_TYPE.NUMBER"
                v-model.positive="timerSettings.interval.every"
                input-class="grey4-grey4-background"
                :min="1"
            />
            <span>{{ $t(`enumT.repeatCycle.appendix.${timerSettings.type}`) }}</span>
        </ion-col>
    </ion-row>

    <ion-row v-if="timerSettings.type === REPEAT_CYCLE.WEEK">
        <template v-for="day of WEEKDAY" :key="day">
            <ion-col size="6" :class="cssClass">
                <checkbox-simple
                    label-class=""
                    :value="timerSettings.interval.days.includes(day)"
                    @update:modelValue="changeTimeDays($event, day)"
                >
                    {{ $t(`enumT.weekday.${day}`) }}
                </checkbox-simple>
            </ion-col>
        </template>
    </ion-row>

    <ion-row class="mt-1" v-if="timerSettings.type === REPEAT_CYCLE.WEEK">
        <ion-col size="3" class="center-y" :class="cssClass">
            {{ $t('label.time') }}
        </ion-col>

        <ion-col>
            <input
                type="time"
                v-model="timerSettings.interval.time"
                class="deactivate-focus purple-purple-color grey4-grey4-background"
                style="border-radius: 8px; min-height: 40px; border: none; padding-left: 10px;"
            />
        </ion-col>
    </ion-row>

    <ion-row>
        <ion-col class="center-x">
            <button-fix
                v-if="!timer.id"
                :disabled="!isValid"
                @click="$emit('save', timerSettings)"
                :class="{'coral-light-background inherit-background light-coral-color': settings}"
            >
                {{ $t('button.add') }}
            </button-fix>

            <button-fix
                v-else
                :disabled="!isValid"
                @click="$emit('edit', timerSettings)"
                :class="{'coral-light-background inherit-background light-coral-color': settings}"
            >
                {{ $t('button.save') }}
            </button-fix>
        </ion-col>
    </ion-row>
</template>

<script setup lang="ts">
import { IonCol, IonRow, IonDatetime } from "@ionic/vue";
import { computed, ref } from "vue";
import { route } from "@/router";
import { DateTime } from "luxon";

import InputSimple from "@/components/input/InputSimple.vue";
import CheckboxSimple from "@/components/checkbox/CheckboxSimple.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";

import { useListStore } from "@/stores/lists";

import { useLanguageArray } from "@/composables/useLanguageArray";

import { Reminder } from "@/types/supplier";
import { REPEAT_CYCLE, WEEKDAY } from "@/types/enum/general";
import { INPUT_TYPE } from "@/types/enum/ionicEnum";
import { TimerSettingsProps } from "@/types/props";
import { TimerSettingsEmits } from "@/types/emits";

const props = defineProps<TimerSettingsProps>()
defineEmits<TimerSettingsEmits>()

const listStore = useListStore()

const today = ref(DateTime.now().toString())
const timerSettings = ref<Reminder>(props.timer)

const { content: repeatOptions } = useLanguageArray([
    { value: REPEAT_CYCLE.ONCE, text: `enumT.repeatCycle.${REPEAT_CYCLE.ONCE}` },
    { value: REPEAT_CYCLE.WEEK, text: `enumT.repeatCycle.${REPEAT_CYCLE.WEEK}` },
], 'text')

const isValid = computed<boolean>(() => {
    if (timerSettings.value.type === REPEAT_CYCLE.ONCE) {
        return Boolean(timerSettings.value.date && (timerSettings.value.listId || route.value.params.listId))
    }
    return Boolean(
        timerSettings.value.interval?.time &&
        timerSettings.value.interval.days.length &&
        timerSettings.value.interval.every &&
        (timerSettings.value.listId || route.value.params.listId)
    )
})

const changeTimeDays = (value: boolean, day: WEEKDAY): void => {
    if (!value) {
        const index = timerSettings.value.interval?.days.findIndex(timerDay => timerDay === day)

        if (index < 0) return

        timerSettings.value.interval?.days.splice(index, 1)
        return
    }

    timerSettings.value.interval?.days.push(day)
}

import { isPlatform } from "@ionic/core";

const ionDateWheelHighlight = computed(() => isPlatform('android') ? '#ffdbea' : '')
const ionDateBorder = computed(() => isPlatform('android') ? '1px solid var(--ion-color-grey3-tint)' : '')

</script>

<style scoped>
.deactivate-focus:focus-visible {
    outline: none;
}
</style>

<style scoped lang="scss">
@use '@/theme/mixin.scss';

.order-filter-month {
  border-radius: 16px;

  @include mixin.light {
    --background: var(--ion-color-light);
    --ion-color-step-150: transparent;
  }

  @include mixin.dark {
    --background: var(--ion-color-purple);
  --ion-color-step-150: transparent;
    }
}

@media (min-width: 650px) {
    .max-width-custom {
		max-width: unset;
	}
	.width-custom {
		display: inline-flex;
		width: 50%;
	}
}


ion-datetime::part(calendar-day disabled) {
    opacity: 0.2;
}

ion-datetime::part(calendar-day today disabled) {
    opacity: 0.6;
}

ion-datetime::part(month-year-button) {
	color: var(--ion-color-coral);
}

ion-datetime {
	--ion-color-step-500: black;
	--ion-color-step-300: dimgray !important;
}

ion-datetime::part(time-button) {
	--ion-color-step-300: var(--ion-color-grey3);
}

ion-datetime { /* only on android */
	--wheel-fade-background-rgb: 255, 255, 255;
	--wheel-highlight-background: v-bind(ionDateWheelHighlight);
	border: v-bind(ionDateBorder);
}

@media (min-width: 500px) and (min-height: 500px) {
    .unset-max-width {
		max-width: unset;
	}
}

@media (orientation: landscape) and (min-height: 500px) {
    .unset-max-width {
		max-width: unset;
	}
}
</style>

<style>
.month-year-picker-open::part(month-year-button):before {
    display: inline-block;
    width: 8px;
    height: 8px;
    content: '';
    border-left: 2px solid;
    border-bottom: 2px solid;
    transform: rotate(45deg);
    margin: 4px 8px 0px 0px;
}
</style>